import React, { useState, useEffect, useCallback } from 'react';
import LORManagerSelector from './LORManagerSelector';
import Icon from '../../components/Icon';
import './LORKeeperTools.css';

const ROUND_LABEL = (r) => r ? `Round ${r}` : '—';
const DRAFT_BASIS_LABEL = (keeper) => keeper.draft_origin === 'undrafted'
  ? (keeper.keeper_cost_label || 'Undrafted — final-round keeper cost pending')
  : `Drafted ${ROUND_LABEL(keeper.original_draft_round)} in 2025`;

const REC_STYLES = {
  keep:      { color: '#4CAF50', bg: 'rgba(76,175,80,0.12)',   label: 'KEEP' },
  consider:  { color: '#FF9F1C', bg: 'rgba(255,159,28,0.12)',  label: 'CONSIDER' },
  available: { color: '#29B6F6', bg: 'rgba(41,182,246,0.12)',  label: 'LIKELY AVAILABLE' },
  pass:      { color: '#888',    bg: 'rgba(100,100,100,0.1)',  label: 'PASS' },
  ineligible:{ color: '#E24B4A', bg: 'rgba(226,75,74,0.12)',   label: 'INELIGIBLE' },
  unknown:   { color: '#7B68EE', bg: 'rgba(123,104,238,0.1)',  label: 'UNKNOWN' },
};

const TABS = [
  { key: 'calculator',   label: '📊 Keeper Calculator',   perManager: true },
  { key: 'surplus',      label: '🏆 League Surplus',       perManager: false },
  { key: 'ineligibility',label: '🚫 Ineligibility',        perManager: false },
  { key: 'declarations', label: '📋 Declarations',         perManager: false },
  { key: 'draftprep',    label: '📅 Draft Prep',           perManager: false },
];

function RecBadge({ rec }) {
  const s = REC_STYLES[rec?.cls] || REC_STYLES.unknown;
  return (
    <span style={{
      display: 'inline-block',
      padding: '2px 10px',
      borderRadius: 12,
      fontSize: '0.7rem',
      fontWeight: 700,
      letterSpacing: '0.05em',
      textTransform: 'uppercase',
      background: s.bg,
      color: s.color,
      border: `1px solid ${s.color}40`,
    }}>
      {rec?.label || 'UNKNOWN'}
    </span>
  );
}

function SurplusBar({ surplus }) {
  if (surplus === null || surplus === undefined) {
    return <span style={{ color: '#555', fontSize: '0.8rem' }}>No ADP</span>;
  }
  const pct   = Math.min(100, Math.max(0, (surplus + 5) / 10 * 100));
  const color = surplus >= 3 ? 'var(--green)' : surplus >= 1 ? '#FF9F1C' : 'var(--red)';
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
      <div style={{ flex: 1, height: 6, background: '#222', borderRadius: 3, overflow: 'hidden' }}>
        <div style={{ width: `${pct}%`, height: '100%', background: color, borderRadius: 3, transition: 'width .3s' }} />
      </div>
      <span style={{ color, fontWeight: 700, fontSize: '0.85rem', minWidth: 36, textAlign: 'right' }}>
        {surplus > 0 ? `+${surplus}` : surplus}
      </span>
    </div>
  );
}

function AvatarCircle({ avatar, name, size = 32 }) {
  return avatar
    ? <img src={`https://sleepercdn.com/avatars/thumbs/${avatar}`} alt={name} className="lor-avatar"
        style={{ width: size, height: size, borderRadius: '50%', objectFit: 'cover', flexShrink: 0 }} />
    : <div style={{ width: size, height: size, borderRadius: '50%', background: '#2a2a2a',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        flexShrink: 0 }}><Icon emoji="👤" size={Math.round(size * 0.45)} /></div>;
}

const RISK_STYLES = {
  Low:    { color: '#4CAF50', bg: 'rgba(76,175,80,0.1)' },
  Medium: { color: '#FF9F1C', bg: 'rgba(255,159,28,0.1)' },
  High:   { color: '#E24B4A', bg: 'rgba(226,75,74,0.1)' },
};

const CONF_STYLES = {
  High:   { color: '#4CAF50' },
  Medium: { color: '#FF9F1C' },
  Low:    { color: '#E24B4A' },
};

function RiskBadge({ level }) {
  if (!level) return null;
  const s = RISK_STYLES[level] || RISK_STYLES.Medium;
  return (
    <span style={{
      display: 'inline-block', padding: '1px 7px', borderRadius: 10,
      fontSize: '0.65rem', fontWeight: 700, letterSpacing: '0.04em',
      background: s.bg, color: s.color, border: `1px solid ${s.color}40`,
    }}>
      {level} Risk
    </span>
  );
}

function ConfidenceDot({ level }) {
  if (!level) return null;
  const s = CONF_STYLES[level] || CONF_STYLES.Medium;
  return (
    <span style={{ fontSize: '0.7rem', color: s.color }}>
      ◉ {level} confidence
    </span>
  );
}

// ── Projected Keeper Board (collapsible) ──────────────────────────────────────

function ProjectedKeeperBoard({ api }) {
  const [open, setOpen]       = useState(false);
  const [data, setData]       = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError]     = useState('');

  const load = useCallback(async () => {
    if (data || loading) return;
    setLoading(true);
    setError('');
    try {
      const r = await fetch(`${api}/api/lor/keepers/projected-board`);
      if (!r.ok) throw new Error(await r.text());
      setData(await r.json());
    } catch (e) {
      setError(e.message || 'Failed to load projected keeper board.');
    }
    setLoading(false);
  }, [api, data, loading]);

  const toggle = () => {
    if (!open) load();
    setOpen(o => !o);
  };

  return (
    <div className="lor-card" style={{ marginTop: 28 }}>
      <button
        onClick={toggle}
        style={{
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          width: '100%', background: 'none', border: 'none', cursor: 'pointer',
          padding: 0, color: 'var(--lor-accent)', fontWeight: 700, fontSize: '0.9rem',
        }}
      >
        <span><Icon emoji="📋" size={13} style={{ verticalAlign: 'middle', marginRight: 4 }} /> Projected Keeper Board <span style={{ color: '#555', fontWeight: 400, fontSize: '0.8rem' }}>(Pre-Declaration)</span></span>
        <span style={{ color: '#555', fontSize: '1rem' }}>{open ? '▲' : '▼'}</span>
      </button>

      {open && (
        <div style={{ marginTop: 16 }}>
          <p style={{ fontSize: '0.78rem', color: '#555', marginBottom: 14 }}>
            Each manager's most likely 2 keepers based on surplus value analysis. Not yet declared — projections only.
          </p>
          {loading && <div className="lor-loading">Loading projected board…</div>}
          {error   && <div className="lor-empty" style={{ color: 'var(--red)' }}>{error}</div>}
          {data && (
            <>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                {(data.projections || []).map(p => (
                  <div key={p.roster_id} style={{
                    padding: '10px 14px', borderRadius: 8,
                    background: p.has_strong_options ? 'rgba(var(--lor-accent-rgb),0.04)' : '#111',
                    border: `1px solid ${p.has_strong_options ? 'rgba(var(--lor-accent-rgb),0.15)' : '#1e1e1e'}`,
                  }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
                      <AvatarCircle avatar={p.avatar} name={p.manager_name} size={26} />
                      <span style={{ fontWeight: 600, color: '#ccc', fontSize: '0.88rem' }}>{p.manager_name}</span>
                      {p.total_projected_surplus > 0 && (
                        <span style={{ marginLeft: 'auto', fontSize: '0.72rem', color: 'var(--green)' }}>
                          +{p.total_projected_surplus} total surplus
                        </span>
                      )}
                    </div>
                    {p.projected_keepers.length === 0 ? (
                      <div style={{ fontSize: '0.78rem', color: '#444' }}>No eligible keeper candidates</div>
                    ) : (
                      <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
                        {p.projected_keepers.map((k, i) => {
                          const recS = REC_STYLES[k.recommendation?.toLowerCase()] || REC_STYLES.unknown;
                          return (
                            <div key={i} style={{
                              flex: '1 1 160px', padding: '7px 10px', borderRadius: 6,
                              background: '#161616', border: `1px solid ${recS.color}30`,
                            }}>
                              <div style={{ fontWeight: 600, color: '#ddd', fontSize: '0.82rem' }}>{k.player_name}</div>
                              <div style={{ fontSize: '0.7rem', color: '#666', marginBottom: 4 }}>
                                {k.position} · {k.team}
                              </div>
                              <div style={{ fontSize: '0.75rem', color: 'var(--lor-accent)' }}>
                                {k.keeper_cost_label || `Costs R${k.keeper_cost_round}`}
                                {k.surplus_rounds != null && (
                                  <span style={{ color: k.surplus_rounds >= 3 ? 'var(--green)' : k.surplus_rounds >= 1 ? '#FF9F1C' : '#888', marginLeft: 6 }}>
                                    {k.surplus_rounds > 0 ? `+${k.surplus_rounds}` : k.surplus_rounds} surplus
                                  </span>
                                )}
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    )}
                    {p.projected_locked_rounds.length > 0 && (
                      <div style={{ marginTop: 6, fontSize: '0.7rem', color: '#555' }}>
                        Locks rounds: {p.projected_locked_rounds.map(r => `R${r}`).join(', ')}
                        {' · '}
                        First open: {p.first_open_rounds.map(r => `R${r}`).join(', ')}
                      </div>
                    )}
                  </div>
                ))}
              </div>

              {data.round_contention && data.round_contention.length > 0 && (
                <div style={{ marginTop: 14, padding: '10px 14px', borderRadius: 8, background: '#111', border: '1px solid #1e1e1e' }}>
                  <div style={{ fontSize: '0.72rem', color: '#555', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 6 }}>
                    Round Contention
                  </div>
                  <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                    {data.round_contention.slice(0, 8).map(([round, count]) => (
                      <span key={round} style={{
                        padding: '2px 8px', borderRadius: 10, fontSize: '0.72rem',
                        background: count >= 3 ? 'rgba(226,75,74,0.1)' : 'rgba(var(--lor-accent-rgb),0.08)',
                        color: count >= 3 ? 'var(--red)' : 'var(--lor-accent)',
                        border: `1px solid ${count >= 3 ? '#E24B4A30' : 'rgba(var(--lor-accent-rgb),0.3)'}`,
                      }}>
                        R{round} — {count} manager{count !== 1 ? 's' : ''}
                      </span>
                    ))}
                  </div>
                </div>
              )}

              <div style={{ marginTop: 10, fontSize: '0.72rem', color: '#3a3a3a' }}>
                Projections based on surplus value analysis. Actual declarations may differ based on positional need and manager preference.
              </div>
            </>
          )}
        </div>
      )}
    </div>
  );
}

// ── Feature 1: Keeper Value Calculator ────────────────────────────────────────

function KeeperCalculator({ api, rosterId }) {
  const [data, setData]     = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError]   = useState('');

  const load = useCallback(async () => {
    if (!rosterId) return;
    setLoading(true);
    setError('');
    try {
      const r = await fetch(`${api}/api/lor/keepers/recommendations/${rosterId}`);
      if (!r.ok) throw new Error(await r.text());
      setData(await r.json());
    } catch (e) {
      setError(e.message || 'Failed to load keeper analysis.');
    }
    setLoading(false);
  }, [api, rosterId]);

  useEffect(() => { load(); }, [load]);

  if (!rosterId) {
    return (
      <div className="lor-empty">
        <div style={{ marginBottom: 8 }}><Icon emoji="📊" size={26} /></div>
        Select your team above to see your keeper values.
      </div>
    );
  }

  if (loading) return <div className="lor-loading">Loading keeper analysis…</div>;
  if (error)   return <div className="lor-empty" style={{ color: 'var(--red)' }}>{error}</div>;
  if (!data)   return null;

  const {
    eligible_keepers = [], ineligible_keepers = [],
    declared_count, max_keepers, manager_name, sleeper_declared = [],
    top_2_recommendation = [], tradeoff_analysis, no_options_warning,
    keep_count = 0, consider_count = 0, available_count = 0,
    adp_stale, adp_age_days, adp_refreshed_at, adp_source_label,
    manager_slot, position_needs = [],
  } = data;

  // Use pick_aware_recommendation when present, fall back to recommendation
  const effectiveRec = (k) => k.pick_aware_recommendation || k.recommendation;

  return (
    <div>
      <div className="lor-page-header">
        <h2 className="lor-page-title" style={{ display: 'flex', alignItems: 'center', gap: 6 }}><Icon emoji="📊" size={18} /> Keeper Value Calculator</h2>
        <span className="lor-page-meta">{manager_name} · {declared_count}/{max_keepers} declared</span>
      </div>

      {adp_stale && (
        <div className="lor-preseason-banner" style={{ borderColor: 'rgba(226,75,74,0.3)', color: 'var(--red)' }}>
          <Icon emoji="⚠️" size={13} style={{ verticalAlign: 'middle', marginRight: 4 }} /> <strong>ADP data is stale{adp_age_days != null ? ` (${adp_age_days} days old)` : ''}.</strong> Availability analysis may be inaccurate.
          Ask the commissioner to refresh keeper data or set ADP overrides in HQ.
        </div>
      )}

      {adp_refreshed_at && (
        <div className="lor-keeper-adp-as-of">
          ADP source: {adp_source_label || 'Sleeper player rankings'} · as of{' '}
          {new Date(adp_refreshed_at).toLocaleString([], {
            month: 'short', day: 'numeric', year: 'numeric', hour: 'numeric', minute: '2-digit',
          })}
        </div>
      )}

      {sleeper_declared.length > 0 && (
        <div className="lor-preseason-banner" style={{ borderColor: 'rgba(76,175,80,0.3)', color: 'var(--green)' }}>
          <Icon emoji="✅" size={13} style={{ verticalAlign: 'middle', marginRight: 4 }} /> <strong>Declared on Sleeper:</strong> {sleeper_declared.length} keeper{sleeper_declared.length !== 1 ? 's' : ''} registered.
          Declared players are marked below.
        </div>
      )}

      {manager_slot && (
        <div style={{ fontSize: '0.75rem', color: '#555', marginBottom: 12 }}>
          Your draft slot: <strong style={{ color: 'var(--lor-accent)' }}>#{manager_slot}</strong>
          {position_needs.length > 0 && (
            <span style={{ marginLeft: 12 }}>
              Positional needs: <strong style={{ color: '#FF9F1C' }}>{position_needs.join(', ')}</strong>
            </span>
          )}
        </div>
      )}

      {no_options_warning && (
        <div className="lor-preseason-banner" style={{ borderColor: 'rgba(226,75,74,0.3)', color: 'var(--red)' }}>
          <Icon emoji="⚠️" size={13} style={{ verticalAlign: 'middle', marginRight: 4 }} /> {no_options_warning}
        </div>
      )}

      {/* Top 2 Recommendations */}
      {!no_options_warning && top_2_recommendation.length > 0 && (
        <div className="lor-card" style={{ marginBottom: 24, borderColor: 'rgba(var(--lor-accent-rgb),0.2)', background: 'rgba(var(--lor-accent-rgb),0.02)' }}>
          <div style={{ fontSize: '0.72rem', color: 'var(--lor-accent)', textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: 12, fontWeight: 700 }}>
            <Icon emoji="🏆" size={12} style={{ verticalAlign: 'middle', marginRight: 3 }} /> Top 2 Recommendations
          </div>

          {tradeoff_analysis && (
            <div style={{
              padding: '10px 14px', borderRadius: 8, marginBottom: 14,
              background: 'rgba(255,159,28,0.06)', border: '1px solid rgba(255,159,28,0.2)',
              fontSize: '0.8rem', color: '#FF9F1C', lineHeight: 1.5,
            }}>
              <Icon emoji="⚖️" size={13} style={{ verticalAlign: 'middle', marginRight: 4 }} /> {tradeoff_analysis}
            </div>
          )}

          <div className="lor-keeper-top-grid">
            {top_2_recommendation.map((k, i) => {
              const rec  = effectiveRec(k);
              const recS = REC_STYLES[rec?.cls] || REC_STYLES.unknown;
              return (
                <div key={k.player_id} className="lor-keeper-top-card" style={{
                  flex: '1 1 220px', padding: '12px 14px', borderRadius: 8,
                  background: '#141414', border: `1px solid ${recS.color}40`,
                }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 6 }}>
                    <span style={{ fontSize: '0.7rem', color: '#555', fontWeight: 700 }}>#{i + 1}</span>
                    <span style={{ fontWeight: 700, color: '#eee', fontSize: '0.95rem' }}>{k.player_name}</span>
                    <span style={{ fontSize: '0.72rem', color: '#888' }}>{k.position} · {k.team}</span>
                    {k.is_declared && (
                      <span style={{ marginLeft: 'auto', fontSize: '0.65rem', fontWeight: 700, color: 'var(--green)' }}><Icon emoji="✓" size={11} style={{ verticalAlign: 'middle', marginRight: 3 }} /> DECLARED</span>
                    )}
                  </div>
                  <div style={{ fontFamily: 'monospace', fontSize: '0.75rem', color: '#666', marginBottom: 8, lineHeight: 1.4 }}>
                    {k.full_math}
                  </div>
                  {k.manager_pick_display_at_adp && (
                    <div style={{ fontSize: '0.72rem', color: '#444', marginBottom: 6 }}>
                      ADP: <strong style={{ color: '#ccc' }}>{k.player_adp_display}</strong>
                      {' · '}Your natural pick: <strong style={{ color: 'var(--lor-accent)' }}>{k.manager_pick_display_at_adp}</strong>
                    </div>
                  )}
                  <div style={{ display: 'flex', gap: 6, alignItems: 'center', flexWrap: 'wrap', marginBottom: 8 }}>
                    <RecBadge rec={rec} />
                    <RiskBadge level={k.risk_level} />
                    <ConfidenceDot level={k.confidence} />
                  </div>
                  <div style={{ fontSize: '0.78rem', color: '#888', lineHeight: 1.5 }}>
                    {k.plain_reasoning || rec?.reasoning}
                  </div>
                  {!k.eligible_next_season_if_kept && (
                    <div className="lor-keeper-terminal-note">Final keeper year — a Round 1 keeper cannot be kept next season.</div>
                  )}
                </div>
              );
            })}
          </div>

          {keep_count + consider_count + available_count > 2 && (
            <div style={{ marginTop: 10, fontSize: '0.75rem', color: '#555' }}>
              {keep_count > 0 && `${keep_count} KEEP`}
              {consider_count > 0 && ` · ${consider_count} CONSIDER`}
              {available_count > 0 && ` · ${available_count} LIKELY AVAILABLE`}
              {' — full breakdown below.'}
            </div>
          )}
        </div>
      )}

      {/* Full ranked player list */}
      {eligible_keepers.length === 0 && (
        <div className="lor-empty">No eligible keeper candidates found for this roster.</div>
      )}

      {eligible_keepers.length > 0 && (
        <div style={{ fontSize: '0.72rem', color: '#444', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 10 }}>
          All Eligible Players — Ranked by Value
        </div>
      )}

      <div style={{ display: 'flex', flexDirection: 'column', gap: 10, marginBottom: 28 }}>
        {eligible_keepers.map(k => {
          const rec  = effectiveRec(k);
          const recS = REC_STYLES[rec?.cls] || REC_STYLES.unknown;
          const avail = k.availability_analysis;
          return (
            <div key={k.player_id} className={`lor-card lor-keeper-player-card${k.is_declared ? ' is-declared' : ''}`} style={{
              borderLeft: `3px solid ${recS.color}`,
              position: 'relative',
            }}>
              {k.is_declared && (
                <div style={{
                  position: 'absolute', top: 10, right: 12,
                  fontSize: '0.7rem', fontWeight: 700, color: 'var(--green)',
                  background: 'rgba(76,175,80,0.12)', padding: '2px 8px', borderRadius: 10,
                  border: '1px solid rgba(76,175,80,0.3)',
                }}><Icon emoji="✓" size={11} style={{ verticalAlign: 'middle', marginRight: 3 }} /> DECLARED</div>
              )}
              <div className="lor-keeper-player-row">
                <div className="lor-keeper-player-copy">
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 3 }}>
                    <span style={{ fontWeight: 700, color: '#eee', fontSize: '1rem' }}>{k.player_name}</span>
                    <span style={{ fontSize: '0.75rem', color: '#888', fontWeight: 600 }}>
                      {k.position} · {k.team}
                    </span>
                    {k.injury_status && (
                      <span style={{ fontSize: '0.68rem', color: 'var(--red)', fontWeight: 700 }}>{k.injury_status}</span>
                    )}
                  </div>
                  <div style={{ fontSize: '0.75rem', color: '#555', marginBottom: 6 }}>
                    {DRAFT_BASIS_LABEL(k)}
                    {k.was_keeper_2025 && <span style={{ color: 'var(--lor-accent)', marginLeft: 8 }}>🔁 was keeper 2025</span>}
                    {k.consecutive_years_kept >= 2 && <span style={{ color: 'var(--lor-accent)', marginLeft: 8 }}>({k.consecutive_years_kept}yr chain)</span>}
                  </div>
                  <div style={{ fontFamily: 'monospace', fontSize: '0.72rem', color: '#555', marginBottom: 7 }}>
                    {k.full_math}
                  </div>

                  {/* Availability row */}
                  {k.player_adp_display && k.manager_pick_display_at_adp && (
                    <div style={{
                      padding: '6px 10px', borderRadius: 6, marginBottom: 8,
                      background: avail?.will_be_available === false
                        ? 'rgba(226,75,74,0.06)' : 'rgba(41,182,246,0.06)',
                      border: `1px solid ${avail?.will_be_available === false ? '#E24B4A' : '#29B6F6'}20`,
                      fontSize: '0.75rem', color: '#777',
                    }}>
                      <span style={{ color: '#aaa' }}>
                        Player ADP: <strong style={{ color: '#fff' }}>{k.player_adp_display}</strong>
                        {' · '}Your pick in that round: <strong style={{ color: 'var(--lor-accent)' }}>{k.manager_pick_display_at_adp}</strong>
                      </span>
                      {avail && (
                        <div style={{ marginTop: 3, color: avail.will_be_available ? '#29B6F6' : 'var(--red)', fontSize: '0.72rem' }}>
                          {avail.will_be_available ? <><Icon emoji="✓" size={12} style={{ verticalAlign: 'middle', marginRight: 3 }} /> Likely available at your natural pick</> : '✗ Likely gone before your natural pick'}
                        </div>
                      )}
                    </div>
                  )}

                  {k.fills_positional_need && (
                    <div style={{ fontSize: '0.72rem', color: '#FF9F1C', marginBottom: 6 }}>
                      <Icon emoji="⚡" size={12} style={{ verticalAlign: 'middle', marginRight: 3 }} /> Fills your {k.position} depth
                    </div>
                  )}
                  {k.other_projected_keeper_same && (
                    <div style={{ fontSize: '0.72rem', color: 'var(--red)', marginBottom: 6 }}>
                      <Icon emoji="⚠️" size={12} style={{ verticalAlign: 'middle', marginRight: 3 }} /> Another manager projected to keep this player
                    </div>
                  )}

                  <div style={{ display: 'flex', gap: 6, alignItems: 'center', flexWrap: 'wrap', marginBottom: 6 }}>
                    <RecBadge rec={rec} />
                    <RiskBadge level={k.risk_level} />
                    <ConfidenceDot level={k.confidence} />
                    {k.adp_source === 'sleeper_search_rank' && (
                      <span style={{ fontSize: '0.62rem', color: '#3a3a3a', fontStyle: 'italic' }}>ADP estimated</span>
                    )}
                  </div>
                  <div style={{ fontSize: '0.78rem', color: '#777', lineHeight: 1.4 }}>
                    {k.plain_reasoning || rec?.reasoning}
                  </div>
                  {!k.eligible_next_season_if_kept && (
                    <div className="lor-keeper-terminal-note">Final keeper year — keeping at R1 makes this player ineligible next season.</div>
                  )}
                </div>

                <div className="lor-keeper-stat-grid">
                  <div className="lor-keeper-stat">
                    <div style={{ fontSize: '0.65rem', color: '#444', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 3 }}>Keep Cost</div>
                    <div style={{ fontWeight: 700, color: 'var(--lor-accent)', fontSize: '1.1rem' }}>R{k.keeper_cost_round}</div>
                    {k.keeper_cost_provisional && (
                      <div style={{ fontSize: '0.65rem', color: '#FF9F1C', marginTop: 1 }}>provisional</div>
                    )}
                    {k.manager_pick_display_at_cost && (
                      <div style={{ fontSize: '0.68rem', color: '#555', marginTop: 1 }}>pick {k.manager_pick_display_at_cost}</div>
                    )}
                  </div>
                  <div className="lor-keeper-stat">
                    <div style={{ fontSize: '0.65rem', color: '#444', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 3 }}>ADP</div>
                    <div style={{ fontWeight: 700, color: '#ccc', fontSize: '1.1rem' }}>
                      {k.player_adp_display || (k.adp_round ? `R${k.adp_round}` : '—')}
                    </div>
                    {k.player_adp_pick_no && (
                      <div style={{ fontSize: '0.68rem', color: '#555', marginTop: 1 }}>pick #{k.player_adp_pick_no}</div>
                    )}
                  </div>
                  <div className="lor-keeper-stat lor-keeper-surplus-stat">
                    <div style={{ fontSize: '0.65rem', color: '#444', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 3 }}>Surplus</div>
                    <SurplusBar surplus={k.surplus_rounds} />
                  </div>
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {ineligible_keepers.length > 0 && (
        <div>
          <h3 style={{ fontSize: '0.8rem', color: '#444', textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: 10 }}>
            Ineligible Players
          </h3>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            {ineligible_keepers.map(k => (
              <div key={k.player_id} className="lor-card" style={{ borderLeft: '3px solid #E24B4A', opacity: 0.65 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 16, flexWrap: 'wrap' }}>
                  <div style={{ flex: 1 }}>
                    <span style={{ fontWeight: 600, color: '#ccc' }}>{k.player_name}</span>
                    <span style={{ fontSize: '0.75rem', color: '#666', marginLeft: 8 }}>{k.position} · {k.team}</span>
                  </div>
                  <RecBadge rec={k.recommendation} />
                  <span style={{ fontSize: '0.78rem', color: '#666' }}>{k.eligibility_reason}</span>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      <div style={{ marginTop: 14, fontSize: '0.72rem', color: '#3a3a3a', lineHeight: 1.5 }}>
        Surplus = keeper cost round − projected ADP round. ADP comes from the daily Sleeper player-ranking cache
        (commissioner can override in HQ). Confidence is High when a commissioner ADP override is set.
      </div>

      <ProjectedKeeperBoard api={api} />
    </div>
  );
}

// ── Feature 2: League Surplus Identifier ──────────────────────────────────────

function LeagueSurplus({ api }) {
  const [data, setData]       = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError]     = useState('');

  useEffect(() => {
    fetch(`${api}/api/lor/keepers/league-surplus`)
      .then(r => r.ok ? r.json() : r.text().then(t => { throw new Error(t); }))
      .then(setData)
      .catch(e => setError(e.message))
      .finally(() => setLoading(false));
  }, [api]);

  if (loading) return <div className="lor-loading">Loading league surplus…</div>;
  if (error)   return <div className="lor-empty" style={{ color: 'var(--red)' }}>{error}</div>;
  if (!data || data.length === 0) {
    return (
      <div className="lor-empty">
        <div style={{ marginBottom: 8 }}><Icon emoji="🏆" size={26} /></div>
        No keeper surplus data available yet.
      </div>
    );
  }

  return (
    <div>
      <div className="lor-page-header">
        <h2 className="lor-page-title" style={{ display: 'flex', alignItems: 'center', gap: 6 }}><Icon emoji="🏆" size={18} /> League Surplus Board</h2>
        <span className="lor-page-meta">Top 10 keeper assets across all teams</span>
      </div>

      <div className="lor-card lor-keeper-table-scroll" style={{ padding: 0 }}>
        <table className="lor-standings-table">
          <thead>
            <tr>
              <th>#</th>
              <th>Player</th>
              <th>Manager</th>
              <th>Cost</th>
              <th>Proj. ADP</th>
              <th>Surplus</th>
              <th>Status</th>
            </tr>
          </thead>
          <tbody>
            {data.map((k, i) => {
              const recS = REC_STYLES[k.recommendation_cls] || REC_STYLES.unknown;
              return (
                <tr key={k.player_id}>
                  <td className="lor-rank">{i + 1}</td>
                  <td>
                    <div style={{ fontWeight: 600, color: '#eee', fontSize: '0.9rem' }}>{k.player_name}</div>
                    <div style={{ fontSize: '0.72rem', color: '#666' }}>{k.position} · {k.team}</div>
                  </td>
                  <td style={{ color: '#ccc', fontSize: '0.85rem' }}>{k.manager_name}</td>
                  <td style={{ fontWeight: 700, color: 'var(--lor-accent)' }}>
                    {ROUND_LABEL(k.keeper_cost_round)}
                    {k.keeper_cost_provisional && <div className="lor-keeper-final-year-table">Provisional</div>}
                    {!k.eligible_next_season_if_kept && <div className="lor-keeper-final-year-table">Final year</div>}
                  </td>
                  <td style={{ color: '#ccc' }}>{k.adp_round ? `Round ${k.adp_round}` : '—'}</td>
                  <td>
                    <SurplusBar surplus={k.surplus_rounds} />
                  </td>
                  <td>
                    {k.is_declared
                      ? <span style={{ color: 'var(--green)', fontWeight: 600, fontSize: '0.78rem' }}>Declared <Icon emoji="✓" size={12} style={{ verticalAlign: 'middle', marginLeft: 3 }} /></span>
                      : <span style={{ color: '#555', fontSize: '0.78rem' }}>Pending</span>}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      <div style={{ marginTop: 12, fontSize: '0.75rem', color: '#444', lineHeight: 1.5 }}>
        Surplus = how many rounds of value you gain by keeping vs. drafting that player openly.
        Higher is better. ADP estimated from Sleeper player rankings.
      </div>
    </div>
  );
}

// ── Feature 3: Ineligibility Checker ──────────────────────────────────────────

function IneligibilityChecker({ api }) {
  const [data, setData]       = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError]     = useState('');
  const [view, setView]       = useState('free-agents'); // 'free-agents' | 'per-roster'

  useEffect(() => {
    fetch(`${api}/api/lor/keepers/ineligibility`)
      .then(r => r.ok ? r.json() : r.text().then(t => { throw new Error(t); }))
      .then(setData)
      .catch(e => setError(e.message))
      .finally(() => setLoading(false));
  }, [api]);

  if (loading) return <div className="lor-loading">Loading ineligibility report…</div>;
  if (error)   return <div className="lor-empty" style={{ color: 'var(--red)' }}>{error}</div>;
  if (!data)   return null;

  const { per_roster = [], league_free_agents = [], total_ineligible } = data;

  return (
    <div>
      <div className="lor-page-header">
        <h2 className="lor-page-title">🚫 Ineligibility Report</h2>
        <span className="lor-page-meta">{total_ineligible} player{total_ineligible !== 1 ? 's' : ''} re-entering the draft pool</span>
      </div>

      <div className="lor-preseason-banner">
        Players who were drafted or kept in Round 1 last season cannot be kept.
        They re-enter the 2026 draft as free picks available to any team.
      </div>

      <div style={{ display: 'flex', gap: 8, marginBottom: 20 }}>
        {[['free-agents', 'Free Agent Pool'], ['per-roster', 'By Roster']].map(([key, label]) => (
          <button key={key}
            className={`lor-comm-tab${view === key ? ' active' : ''}`}
            onClick={() => setView(key)}
          >{label}</button>
        ))}
      </div>

      {view === 'free-agents' && (
        <div className="lor-card lor-keeper-table-scroll" style={{ padding: 0 }}>
          {league_free_agents.length === 0 ? (
            <div className="lor-empty" style={{ padding: '24px 0' }}>No ineligible players found.</div>
          ) : (
            <table className="lor-standings-table">
              <thead>
                <tr>
                  <th>Player</th>
                  <th>Currently Held By</th>
                  <th>2025 Round</th>
                  <th>Reason</th>
                  <th>2026 Status</th>
                </tr>
              </thead>
              <tbody>
                {league_free_agents.map(fa => (
                  <tr key={fa.player_id}>
                    <td>
                      <div style={{ fontWeight: 600, color: '#eee' }}>{fa.player_name}</div>
                      <div style={{ fontSize: '0.72rem', color: '#666' }}>{fa.position} · {fa.team}</div>
                    </td>
                    <td style={{ color: '#ccc', fontSize: '0.85rem' }}>{fa.held_by}</td>
                    <td style={{ color: 'var(--lor-accent)', fontWeight: 700 }}>Round {fa.draft_round_2025}</td>
                    <td style={{ fontSize: '0.8rem', color: '#888' }}>{fa.reason}</td>
                    <td>
                      <span style={{ color: 'var(--green)', fontSize: '0.78rem', fontWeight: 600 }}>
                        Free pick — open draft
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      )}

      {view === 'per-roster' && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
          {per_roster.filter(r => r.ineligible_count > 0).map(r => (
            <div key={r.roster_id} className="lor-card">
              <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 12 }}>
                <AvatarCircle avatar={r.avatar} name={r.manager_name} size={32} />
                <div>
                  <div style={{ fontWeight: 600, color: '#eee' }}>{r.manager_name}</div>
                  <div style={{ fontSize: '0.75rem', color: '#888' }}>
                    {r.ineligible_count} ineligible player{r.ineligible_count !== 1 ? 's' : ''}
                  </div>
                </div>
              </div>
              {r.ineligible.map(p => (
                <div key={p.player_id} style={{
                  display: 'flex', alignItems: 'center', gap: 12, padding: '6px 0',
                  borderTop: '1px solid #1a1a1a', fontSize: '0.85rem',
                }}>
                  <span style={{ flex: 1, fontWeight: 500, color: '#ccc' }}>{p.player_name}</span>
                  <span style={{ color: '#666' }}>{p.position} · {p.team}</span>
                  <span style={{ color: '#888', fontSize: '0.78rem' }}>{p.reason}</span>
                </div>
              ))}
            </div>
          ))}
          {per_roster.every(r => r.ineligible_count === 0) && (
            <div className="lor-empty">No ineligible players found on any roster.</div>
          )}
        </div>
      )}
    </div>
  );
}

// ── Feature 4: Declaration Tracker ────────────────────────────────────────────

function DeclarationTracker({ api }) {
  const [data, setData]       = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError]     = useState('');

  useEffect(() => {
    fetch(`${api}/api/lor/keepers/declarations`)
      .then(r => r.ok ? r.json() : r.text().then(t => { throw new Error(t); }))
      .then(setData)
      .catch(e => setError(e.message))
      .finally(() => setLoading(false));
  }, [api]);

  if (loading) return <div className="lor-loading">Loading declaration status…</div>;
  if (error)   return <div className="lor-empty" style={{ color: 'var(--red)' }}>{error}</div>;
  if (!data)   return null;

  const {
    managers = [], declared_count, total_managers, deadline_date, deadline_label,
    deadline_status, days_until_deadline, all_declared, draft_scheduled,
  } = data;
  const pending = managers.filter(m => !m.has_declared);
  const declared = managers.filter(m => m.has_declared);

  const deadlineColor = days_until_deadline !== null
    ? (days_until_deadline <= 3 ? 'var(--red)' : days_until_deadline <= 7 ? 'var(--accent2)' : 'var(--lor-accent)')
    : '#666';

  return (
    <div>
      <div className="lor-page-header">
        <h2 className="lor-page-title" style={{ display: 'flex', alignItems: 'center', gap: 6 }}><Icon emoji="📋" size={18} /> Declaration Tracker</h2>
        <span className="lor-page-meta">{declared_count}/{total_managers} declared</span>
      </div>

      <div className="lor-keeper-declaration-summary">
        <div className="lor-card" style={{ flex: 1, minWidth: 140, textAlign: 'center', padding: '16px 20px' }}>
          <div style={{ fontSize: '2rem', fontWeight: 700, color: 'var(--lor-accent)' }}>{declared_count}/{total_managers}</div>
          <div style={{ fontSize: '0.78rem', color: '#666', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Managers Declared</div>
        </div>
        <div className="lor-card" style={{ flex: 1, minWidth: 140, textAlign: 'center', padding: '16px 20px' }}>
          {draft_scheduled && days_until_deadline !== null ? (
            <>
              <div style={{ fontSize: '2rem', fontWeight: 700, color: deadlineColor }}>{days_until_deadline}</div>
              <div style={{ fontSize: '0.78rem', color: '#666', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                {deadline_status === 'closed' ? 'Deadline Passed' : 'Days Until Deadline'}
              </div>
              {deadline_date && <div style={{ fontSize: '0.7rem', color: '#555', marginTop: 4 }}>{deadline_date}</div>}
            </>
          ) : (
            <>
              <div style={{ fontSize: '1.2rem', fontWeight: 700, color: '#555' }}>TBD</div>
              <div style={{ fontSize: '0.78rem', color: '#666', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Deadline Not Yet Available</div>
            </>
          )}
        </div>
        {all_declared && (
          <div className="lor-card" style={{ flex: 1, minWidth: 140, textAlign: 'center', padding: '16px 20px',
            background: 'rgba(76,175,80,0.06)', borderColor: 'rgba(76,175,80,0.2)' }}>
            <div><Icon emoji="✅" size={28} /></div>
            <div style={{ fontSize: '0.78rem', color: 'var(--green)', textTransform: 'uppercase', letterSpacing: '0.05em' }}>All Declared</div>
          </div>
        )}
      </div>

      {deadline_label && <div className="lor-keeper-deadline-label">{deadline_label}</div>}

      {pending.length > 0 && (
        <div style={{ marginBottom: 24 }}>
          <h3 style={{ fontSize: '0.8rem', color: 'var(--red)', textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: 10 }}>
            <Icon emoji="⏳" size={13} style={{ verticalAlign: 'middle', marginRight: 3 }} /> Pending ({pending.length})
          </h3>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            {pending.map(m => (
              <div key={m.roster_id} className="lor-card lor-keeper-declaration-row" style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '12px 16px' }}>
                <AvatarCircle avatar={m.avatar} name={m.manager_name} size={32} />
                <span style={{ fontWeight: 500, color: '#ccc', flex: 1 }}>{m.manager_name}</span>
                <span style={{ fontSize: '0.78rem', color: '#555' }}>0/{m.max_keepers} declared</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {declared.length > 0 && (
        <div>
          <h3 style={{ fontSize: '0.8rem', color: 'var(--green)', textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: 10 }}>
            <Icon emoji="✅" size={13} style={{ verticalAlign: 'middle', marginRight: 3 }} /> Declared ({declared.length})
          </h3>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
            {declared.map(m => (
              <div key={m.roster_id} className="lor-card">
                <div className="lor-keeper-declaration-head" style={{ marginBottom: m.declared_details.length ? 10 : 0 }}>
                  <AvatarCircle avatar={m.avatar} name={m.manager_name} size={32} />
                  <div style={{ flex: 1 }}>
                    <div style={{ fontWeight: 600, color: '#eee' }}>{m.manager_name}</div>
                    <div style={{ fontSize: '0.75rem', color: 'var(--green)' }}>{m.declared_count}/{m.max_keepers} declared</div>
                  </div>
                  {m.hq_received && (
                    <span style={{ fontSize: '0.7rem', color: 'var(--green)', background: 'rgba(76,175,80,0.1)',
                      padding: '2px 8px', borderRadius: 10, border: '1px solid rgba(76,175,80,0.3)' }}>
                      HQ confirmed
                    </span>
                  )}
                </div>
                {m.declared_details.map(d => (
                  <div key={d.player_id} className="lor-keeper-declaration-detail" style={{
                    display: 'flex', alignItems: 'center', gap: 10, padding: '5px 0',
                    borderTop: '1px solid #1a1a1a', fontSize: '0.85rem',
                  }}>
                    <span style={{ flex: 1, fontWeight: 500, color: '#ccc' }}>{d.player_name}</span>
                    <span style={{ color: '#666', fontSize: '0.78rem' }}>{d.position} · {d.team}</span>
                    <span style={{ color: 'var(--lor-accent)', fontWeight: 700, fontSize: '0.8rem' }}>
                      {d.keeper_cost_label || (d.keeper_cost_round ? `Costs R${d.keeper_cost_round}` : '—')}
                    </span>
                  </div>
                ))}
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

// ── Feature 5: Draft Prep ──────────────────────────────────────────────────────

function DraftPrep({ api }) {
  const [data, setData]       = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError]     = useState('');

  useEffect(() => {
    fetch(`${api}/api/lor/keepers/draft-prep`)
      .then(r => r.ok ? r.json() : r.text().then(t => { throw new Error(t); }))
      .then(setData)
      .catch(e => setError(e.message))
      .finally(() => setLoading(false));
  }, [api]);

  if (loading) return <div className="lor-loading">Loading draft prep data…</div>;
  if (error)   return <div className="lor-empty" style={{ color: 'var(--red)' }}>{error}</div>;
  if (!data)   return null;

  const { round_summary = [], declared_keepers = [], free_agents = [], locked_rounds, open_rounds, declared_count } = data;

  return (
    <div>
      <div className="lor-page-header">
        <h2 className="lor-page-title" style={{ display: 'flex', alignItems: 'center', gap: 6 }}><Icon emoji="📅" size={18} /> Draft Prep Integration</h2>
        <span className="lor-page-meta">{declared_count} keeper slot{declared_count !== 1 ? 's' : ''} locked · {open_rounds.length} open rounds</span>
      </div>

      <div style={{ display: 'flex', gap: 16, marginBottom: 24, flexWrap: 'wrap' }}>
        <div className="lor-card" style={{ flex: 1, minWidth: 120, textAlign: 'center' }}>
          <div style={{ fontSize: '1.8rem', fontWeight: 700, color: 'var(--red)' }}>{locked_rounds?.length || 0}</div>
          <div style={{ fontSize: '0.75rem', color: '#666', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Rounds with Keepers</div>
        </div>
        <div className="lor-card" style={{ flex: 1, minWidth: 120, textAlign: 'center' }}>
          <div style={{ fontSize: '1.8rem', fontWeight: 700, color: 'var(--green)' }}>{open_rounds?.length || 0}</div>
          <div style={{ fontSize: '0.75rem', color: '#666', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Fully Open Rounds</div>
        </div>
        <div className="lor-card" style={{ flex: 1, minWidth: 120, textAlign: 'center' }}>
          <div style={{ fontSize: '1.8rem', fontWeight: 700, color: '#FF9F1C' }}>{free_agents.length}</div>
          <div style={{ fontSize: '0.75rem', color: '#666', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Free Agents in Pool</div>
        </div>
      </div>

      <div className="lor-card" style={{ marginBottom: 20 }}>
        <div className="lor-card-title" style={{ marginBottom: 12 }}>Round-by-Round Board</div>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))', gap: 8 }}>
          {round_summary.map(r => (
            <div key={r.round} style={{
              padding: '10px 14px',
              borderRadius: 8,
              background: r.is_locked ? 'rgba(var(--lor-accent-rgb),0.06)' : '#111',
              border: `1px solid ${r.is_locked ? 'rgba(var(--lor-accent-rgb),0.2)' : '#1e1e1e'}`,
            }}>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: r.is_locked ? 8 : 0 }}>
                <span style={{ fontWeight: 700, color: r.is_locked ? 'var(--lor-accent)' : '#555' }}>Round {r.round}</span>
                <span style={{ fontSize: '0.72rem', color: r.is_locked ? 'var(--lor-accent)' : '#333' }}>
                  {r.is_locked ? `${r.locked_picks.length} locked / ${r.open_picks} open` : `${r.open_picks} open picks`}
                </span>
              </div>
              {r.is_locked && r.locked_picks.map(p => (
                <div key={p.player_id} style={{ fontSize: '0.78rem', color: '#aaa', marginTop: 2 }}>
                  <Icon emoji="🔒" size={12} style={{ verticalAlign: 'middle', marginRight: 3 }} /> <strong>{p.player_name}</strong> ({p.position}) — {p.manager_name}
                </div>
              ))}
            </div>
          ))}
        </div>
      </div>

      {free_agents.length > 0 && (
        <div className="lor-card">
          <div className="lor-card-title" style={{ marginBottom: 12 }}>
            Free Agents Entering Draft Pool ({free_agents.length})
          </div>
          <p style={{ fontSize: '0.8rem', color: '#666', marginBottom: 14 }}>
            These players were ineligible to be kept and re-enter the draft as free picks available to any team.
          </p>
          <div className="lor-card lor-keeper-table-scroll" style={{ padding: 0 }}>
            <table className="lor-standings-table">
              <thead>
                <tr><th>Player</th><th>Held By</th><th>Reason</th></tr>
              </thead>
              <tbody>
                {free_agents.map(fa => (
                  <tr key={fa.player_id}>
                    <td>
                      <div style={{ fontWeight: 600, color: '#eee' }}>{fa.player_name}</div>
                      <div style={{ fontSize: '0.72rem', color: '#666' }}>{fa.position} · {fa.team}</div>
                    </td>
                    <td style={{ color: '#ccc', fontSize: '0.85rem' }}>{fa.held_by}</td>
                    <td style={{ color: '#888', fontSize: '0.78rem' }}>{fa.reason}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}

// ── Main Keeper Tools wrapper ──────────────────────────────────────────────────

export default function LORKeeperTools({ api }) {
  const [tab, setTab]           = useState('calculator');
  const [rosterId, setRosterId] = useState(null);
  const activeTab               = TABS.find(t => t.key === tab) || TABS[0];

  return (
    <div className="lor-keeper-tools">
      <div className="lor-page-header">
        <h2 className="lor-page-title">🔑 Keeper Tools</h2>
        <span className="lor-page-meta">2026 Season · 2 keepers per team · Draft −1 round</span>
      </div>

      {activeTab.perManager && (
        <div style={{ marginBottom: 20 }}>
          <LORManagerSelector
            api={api}
            onSelect={setRosterId}
            selectedRosterId={rosterId}
          />
        </div>
      )}

      <div className="lor-comm-tabs lor-keeper-tabs" style={{ marginBottom: 24 }}>
        {TABS.map(t => (
          <button
            key={t.key}
            className={`lor-comm-tab${tab === t.key ? ' active' : ''}`}
            onClick={() => setTab(t.key)}
          >
            {t.label}
          </button>
        ))}
      </div>

      {tab === 'calculator'    && <KeeperCalculator api={api} rosterId={rosterId} />}
      {tab === 'surplus'       && <LeagueSurplus api={api} />}
      {tab === 'ineligibility' && <IneligibilityChecker api={api} />}
      {tab === 'declarations'  && <DeclarationTracker api={api} />}
      {tab === 'draftprep'     && <DraftPrep api={api} />}
    </div>
  );
}
